/**
 * Theme Presets for shadcn/ui CSS Variable System
 *
 * Three preset palettes: earthy (default), modern, western.
 * Change ACTIVE_THEME to switch the entire app's color scheme.
 *
 * Values are HSL components (without 'hsl()' wrapper) for use
 * as CSS custom properties with Tailwind's hsl() convention.
 */

export type ThemePreset = 'earthy' | 'modern' | 'western' | 'rodeo';

// *** CHANGE THIS ONE LINE TO SWITCH THEMES ***
export const ACTIVE_THEME: ThemePreset = 'rodeo';

export interface ThemeVariables {
  background: string;
  foreground: string;
  card: string;
  'card-foreground': string;
  popover: string;
  'popover-foreground': string;
  primary: string;
  'primary-foreground': string;
  secondary: string;
  'secondary-foreground': string;
  muted: string;
  'muted-foreground': string;
  accent: string;
  'accent-foreground': string;
  destructive: string;
  'destructive-foreground': string;
  border: string;
  input: string;
  ring: string;
  radius: string;
  'sidebar-background': string;
  'sidebar-foreground': string;
  'sidebar-border': string;
  'sidebar-accent': string;
  'sidebar-accent-foreground': string;
  'header-background': string;
  'header-foreground': string;
}

export interface ThemePresetConfig {
  light: ThemeVariables;
  dark: ThemeVariables;
}

export const themePresets: Record<ThemePreset, ThemePresetConfig> = {
  /**
   * Earthy/Warm — sage greens, warm browns, cream backgrounds
   * Default theme for HighCall (western/rodeo app)
   */
  earthy: {
    light: {
      background: '40 33% 98%',
      foreground: '30 10% 15%',
      card: '40 25% 97%',
      'card-foreground': '30 10% 15%',
      popover: '0 0% 100%',
      'popover-foreground': '30 10% 15%',
      primary: '142 40% 35%',
      'primary-foreground': '0 0% 98%',
      secondary: '30 20% 90%',
      'secondary-foreground': '30 10% 25%',
      muted: '35 15% 93%',
      'muted-foreground': '30 8% 46%',
      accent: '30 25% 88%',
      'accent-foreground': '30 10% 20%',
      destructive: '0 72% 51%',
      'destructive-foreground': '0 0% 98%',
      border: '30 15% 85%',
      input: '30 15% 85%',
      ring: '142 40% 35%',
      radius: '0.5rem',
      'sidebar-background': '0 0% 100%',
      'sidebar-foreground': '30 10% 30%',
      'sidebar-border': '30 10% 90%',
      'sidebar-accent': '142 40% 92%',
      'sidebar-accent-foreground': '142 40% 25%',
      'header-background': '30 18% 25%',
      'header-foreground': '35 15% 92%',
    },
    dark: {
      background: '30 10% 10%',
      foreground: '35 15% 90%',
      card: '30 10% 12%',
      'card-foreground': '35 15% 90%',
      popover: '30 10% 12%',
      'popover-foreground': '35 15% 90%',
      primary: '142 45% 45%',
      'primary-foreground': '0 0% 98%',
      secondary: '30 10% 18%',
      'secondary-foreground': '35 15% 85%',
      muted: '30 8% 18%',
      'muted-foreground': '30 8% 55%',
      accent: '30 12% 22%',
      'accent-foreground': '35 15% 90%',
      destructive: '0 72% 51%',
      'destructive-foreground': '0 0% 98%',
      border: '30 8% 22%',
      input: '30 8% 22%',
      ring: '142 45% 45%',
      radius: '0.5rem',
      'sidebar-background': '30 10% 8%',
      'sidebar-foreground': '35 15% 85%',
      'sidebar-border': '30 8% 18%',
      'sidebar-accent': '142 40% 35%',
      'sidebar-accent-foreground': '0 0% 98%',
      'header-background': '30 10% 14%',
      'header-foreground': '35 15% 90%',
    },
  },

  /**
   * Modern — cool slate/zinc with blue accents
   * Clean, professional look
   */
  modern: {
    light: {
      background: '0 0% 100%',
      foreground: '222 47% 11%',
      card: '0 0% 100%',
      'card-foreground': '222 47% 11%',
      popover: '0 0% 100%',
      'popover-foreground': '222 47% 11%',
      primary: '221 83% 53%',
      'primary-foreground': '210 40% 98%',
      secondary: '210 40% 96%',
      'secondary-foreground': '222 47% 11%',
      muted: '210 40% 96%',
      'muted-foreground': '215 16% 47%',
      accent: '210 40% 96%',
      'accent-foreground': '222 47% 11%',
      destructive: '0 72% 51%',
      'destructive-foreground': '210 40% 98%',
      border: '214 32% 91%',
      input: '214 32% 91%',
      ring: '221 83% 53%',
      radius: '0.5rem',
      'sidebar-background': '222 47% 11%',
      'sidebar-foreground': '213 31% 91%',
      'sidebar-border': '217 33% 17%',
      'sidebar-accent': '221 83% 53%',
      'sidebar-accent-foreground': '210 40% 98%',
      'header-background': '210 40% 96%',
      'header-foreground': '222 47% 11%',
    },
    dark: {
      background: '222 47% 6%',
      foreground: '210 40% 98%',
      card: '222 47% 8%',
      'card-foreground': '210 40% 98%',
      popover: '222 47% 8%',
      'popover-foreground': '210 40% 98%',
      primary: '217 91% 60%',
      'primary-foreground': '222 47% 6%',
      secondary: '217 33% 17%',
      'secondary-foreground': '210 40% 98%',
      muted: '217 33% 17%',
      'muted-foreground': '215 20% 55%',
      accent: '217 33% 17%',
      'accent-foreground': '210 40% 98%',
      destructive: '0 72% 51%',
      'destructive-foreground': '210 40% 98%',
      border: '217 33% 17%',
      input: '217 33% 17%',
      ring: '217 91% 60%',
      radius: '0.5rem',
      'sidebar-background': '222 47% 4%',
      'sidebar-foreground': '213 31% 85%',
      'sidebar-border': '217 33% 12%',
      'sidebar-accent': '217 91% 55%',
      'sidebar-accent-foreground': '210 40% 98%',
      'header-background': '222 47% 10%',
      'header-foreground': '210 40% 92%',
    },
  },

  /**
   * Western — deep maroon, leather brown, parchment
   * Bold rodeo/western feel
   */
  western: {
    light: {
      background: '30 25% 96%',
      foreground: '0 10% 15%',
      card: '30 20% 95%',
      'card-foreground': '0 10% 15%',
      popover: '0 0% 100%',
      'popover-foreground': '0 10% 15%',
      primary: '348 60% 35%',
      'primary-foreground': '0 0% 98%',
      secondary: '25 30% 88%',
      'secondary-foreground': '0 10% 20%',
      muted: '25 18% 91%',
      'muted-foreground': '0 5% 46%',
      accent: '25 35% 85%',
      'accent-foreground': '0 10% 18%',
      destructive: '0 72% 51%',
      'destructive-foreground': '0 0% 98%',
      border: '25 18% 82%',
      input: '25 18% 82%',
      ring: '348 60% 35%',
      radius: '0.375rem',
      'sidebar-background': '0 12% 15%',
      'sidebar-foreground': '25 20% 88%',
      'sidebar-border': '0 10% 22%',
      'sidebar-accent': '348 55% 32%',
      'sidebar-accent-foreground': '0 0% 98%',
      'header-background': '25 20% 90%',
      'header-foreground': '0 10% 15%',
    },
    dark: {
      background: '0 10% 8%',
      foreground: '25 20% 90%',
      card: '0 10% 10%',
      'card-foreground': '25 20% 90%',
      popover: '0 10% 10%',
      'popover-foreground': '25 20% 90%',
      primary: '348 65% 48%',
      'primary-foreground': '0 0% 98%',
      secondary: '0 8% 16%',
      'secondary-foreground': '25 20% 85%',
      muted: '0 8% 16%',
      'muted-foreground': '0 5% 55%',
      accent: '0 10% 20%',
      'accent-foreground': '25 20% 90%',
      destructive: '0 72% 51%',
      'destructive-foreground': '0 0% 98%',
      border: '0 8% 20%',
      input: '0 8% 20%',
      ring: '348 65% 48%',
      radius: '0.375rem',
      'sidebar-background': '0 10% 6%',
      'sidebar-foreground': '25 18% 82%',
      'sidebar-border': '0 8% 14%',
      'sidebar-accent': '348 60% 38%',
      'sidebar-accent-foreground': '0 0% 98%',
      'header-background': '0 10% 12%',
      'header-foreground': '25 18% 88%',
    },
  },

  /**
   * Rodeo — Deep slate with crimson accents
   * Modern sports-app feel with HighCall brand colors
   */
  rodeo: {
    light: {
      background: '210 20% 98%',
      foreground: '220 15% 15%',
      card: '0 0% 100%',
      'card-foreground': '220 15% 15%',
      popover: '0 0% 100%',
      'popover-foreground': '220 15% 15%',
      primary: '0 65% 45%',
      'primary-foreground': '0 0% 100%',
      secondary: '210 15% 93%',
      'secondary-foreground': '220 15% 20%',
      muted: '210 12% 94%',
      'muted-foreground': '220 8% 46%',
      accent: '210 18% 90%',
      'accent-foreground': '220 15% 18%',
      destructive: '0 72% 51%',
      'destructive-foreground': '0 0% 98%',
      border: '210 15% 88%',
      input: '210 15% 88%',
      ring: '0 65% 45%',
      radius: '0.5rem',
      'sidebar-background': '220 20% 18%',
      'sidebar-foreground': '210 15% 85%',
      'sidebar-border': '220 15% 24%',
      'sidebar-accent': '0 65% 45%',
      'sidebar-accent-foreground': '0 0% 100%',
      'header-background': '220 20% 18%',
      'header-foreground': '210 15% 92%',
    },
    dark: {
      background: '220 18% 8%',
      foreground: '210 15% 92%',
      card: '220 18% 10%',
      'card-foreground': '210 15% 92%',
      popover: '220 18% 10%',
      'popover-foreground': '210 15% 92%',
      primary: '0 70% 55%',
      'primary-foreground': '0 0% 100%',
      secondary: '220 15% 15%',
      'secondary-foreground': '210 15% 85%',
      muted: '220 12% 15%',
      'muted-foreground': '220 8% 55%',
      accent: '220 15% 18%',
      'accent-foreground': '210 15% 92%',
      destructive: '0 72% 51%',
      'destructive-foreground': '0 0% 98%',
      border: '220 12% 18%',
      input: '220 12% 18%',
      ring: '0 70% 55%',
      radius: '0.5rem',
      'sidebar-background': '220 20% 6%',
      'sidebar-foreground': '210 15% 82%',
      'sidebar-border': '220 12% 14%',
      'sidebar-accent': '0 70% 50%',
      'sidebar-accent-foreground': '0 0% 100%',
      'header-background': '220 18% 12%',
      'header-foreground': '210 15% 90%',
    },
  },
};
